/**
 * "Foreground service killed" banner policy. Native plugin returns raw
 * facts; policy here decides when/how to show the banner.
 *
 *   killed: 2× poll interval since last heartbeat
 *   start fail: no heartbeat after 15s grace
 *   rate limit: 24h between shows (unless a fresh kill arrives)
 *   suppress: no active network
 */

import { ref, type Ref } from "vue";

import { isAndroid } from "./platform";
// Type-only import keeps ip-change-plugin out of this module's static graph
// so it can be code-split. The runtime value comes from a dynamic import
// below, matching the pattern in force-logout.ts / server-url.ts.
import type { ServiceHealth } from "./ip-change-plugin";
import { isBatteryOptimizationExempt, openOemSettings } from "./battery-optimization";
import { remediationFor, type OemKind } from "./oem-remediation";

const LAST_SHOWN_KEY = "knocknoc_fgs_kill_banner_last_shown_ts";
const MIN_SHOW_INTERVAL_MS = 4 * 60 * 60 * 1000;
const INITIAL_START_GRACE_MS = 15 * 1000;
const KILL_MULTIPLIER = 2;

export interface ServiceKilledState {
  kind: OemKind;
  i18nPrefix: string;
  batteryOptExempt: boolean;
  manufacturer: string;
  model: string;
}

export const serviceKilledState: Ref<ServiceKilledState | null> = ref(null);

async function readLastShown(): Promise<number> {
  try {
    const { Preferences } = await import("@capacitor/preferences");
    const { value } = await Preferences.get({ key: LAST_SHOWN_KEY });
    if (!value) return 0;
    const n = Number(value);
    return Number.isFinite(n) ? n : 0;
  } catch {
    return 0;
  }
}

async function writeLastShown(ts: number): Promise<void> {
  try {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.set({ key: LAST_SHOWN_KEY, value: String(ts) });
  } catch {
    // Missed write may show the banner sooner than 24h, acceptable.
  }
}

export function isKilled(h: ServiceHealth): boolean {
  if (!h.expectedRunning) return false;

  // No heartbeat after grace → OEM-killed before first tick.
  if (h.lastHeartbeatTs === 0) {
    return h.expectedStartedAtTs > 0 && h.nowTs - h.expectedStartedAtTs > INITIAL_START_GRACE_MS;
  }

  // Clock went backwards (DST/manual change); not a kill.
  if (h.nowTs < h.lastHeartbeatTs) return false;

  const sinceLastHeartbeat = h.nowTs - h.lastHeartbeatTs;
  const killWindow = KILL_MULTIPLIER * h.expectedIntervalSeconds * 1000;
  return sinceLastHeartbeat > killWindow;
}

// Call on resume; updates serviceKilledState. No-op on web.
export async function recheckServiceHealth(): Promise<void> {
  if (!isAndroid()) {
    serviceKilledState.value = null;
    return;
  }

  let health: ServiceHealth;
  try {
    const { default: IpChange } = await import("./ip-change-plugin");
    health = await IpChange.checkServiceHealth();
  } catch {
    // Plugin unavailable (tests, older APK): skip.
    serviceKilledState.value = null;
    return;
  }

  if (!isKilled(health)) {
    serviceKilledState.value = null;
    return;
  }

  // No network → user already knows access is broken.
  if (!health.hasActiveNetwork) {
    serviceKilledState.value = null;
    return;
  }

  const lastShown = await readLastShown();
  const freshKill = health.lastHeartbeatTs > lastShown;
  const withinSilence = lastShown > 0 && health.nowTs - lastShown < MIN_SHOW_INTERVAL_MS;

  if (withinSilence && !freshKill) {
    serviceKilledState.value = null;
    return;
  }

  const remediation = remediationFor(health.manufacturer);
  const exempt = await isBatteryOptimizationExempt();

  serviceKilledState.value = {
    kind: remediation.kind,
    i18nPrefix: remediation.i18nPrefix,
    batteryOptExempt: exempt,
    manufacturer: health.manufacturer,
    model: health.model,
  };
  await writeLastShown(health.nowTs);
}

export function dismissServiceKilledBanner(): void {
  serviceKilledState.value = null;
}

let _watcherInstalled = false;

// Idempotent. Immediate check + resume listener so a cold-start kill surfaces.
export async function initServiceHealthWatcher(): Promise<void> {
  if (!isAndroid() || _watcherInstalled) return;
  _watcherInstalled = true;

  recheckServiceHealth();

  const { App } = await import("@capacitor/app");
  await App.addListener("appStateChange", (state) => {
    if (state.isActive) {
      recheckServiceHealth();
    }
  });
}

// Native falls back to the app details screen if the ComponentName no longer resolves.
export async function openServiceKilledRemediation(): Promise<boolean> {
  const state = serviceKilledState.value;
  if (!state) return false;
  const remediation = remediationFor(state.manufacturer);
  if (remediation.intent) {
    return openOemSettings(remediation.intent);
  }
  return openOemSettings({});
}
